import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { Platform } from 'react-native';
import 'intl-pluralrules';
import { getDeviceLanguage } from './src/utils/deviceLanguage';
import {
	EN_RESOURCES,
	ensureLocaleLoaded,
	readPersistedLanguage,
} from './i18n/lazyLocales';

const initialLng = Platform.OS !== 'web' ? getDeviceLanguage() : undefined;

i18n.use(initReactI18next).init({
	resources: EN_RESOURCES,
	lng: initialLng ?? 'en-GB',
	fallbackLng: 'en-GB',
	interpolation: {
		escapeValue: false,
	},
});

const NATIVE_CHANGE_LANGUAGE_KEY = '__smartsaasNativeChangeLanguage';

type I18nWithNative = typeof i18n & {
	[NATIVE_CHANGE_LANGUAGE_KEY]?: typeof i18n.changeLanguage;
	__smartsaasChangeLanguagePatched?: boolean;
};

const i18nRef = i18n as I18nWithNative;

function getNativeChangeLanguage(): typeof i18n.changeLanguage {
	if (!i18nRef[NATIVE_CHANGE_LANGUAGE_KEY]) {
		i18nRef[NATIVE_CHANGE_LANGUAGE_KEY] = i18n.changeLanguage.bind(i18n);
	}
	return i18nRef[NATIVE_CHANGE_LANGUAGE_KEY]!;
}

/** Internal: apply language after bundles are loaded (bypasses the public wrapper). */
async function applyI18nLanguage(locale: string): Promise<void> {
	if (i18n.language === locale && i18n.resolvedLanguage === locale) {
		return;
	}
	await getNativeChangeLanguage()(locale);
}

export async function changeAppLanguage(lng?: string | null): Promise<void> {
	const locale = await ensureLocaleLoaded(lng);
	if (__DEV__) {
		console.log(
			'[i18n] changeAppLanguage',
			JSON.stringify({ requested: lng, locale, current: i18n.language })
		);
	}
	await applyI18nLanguage(locale);
}

/** Legacy default in Redux before the user picks a locale in settings. */
const LEGACY_DEFAULT_LANGUAGE = 'English';

function readReduxPersistedLanguage(): string | null {
	try {
		const { store } = require('@reduxLocal/persistState') as typeof import('@reduxLocal/persistState');
		const appLanguage = store.getState()?.app?.language;
		if (!appLanguage) return null;
		const raw =
			typeof appLanguage === 'string'
				? appLanguage
				: appLanguage?.value;
		if (typeof raw !== 'string') return null;
		const trimmed = raw.trim();
		if (!trimmed || trimmed === LEGACY_DEFAULT_LANGUAGE) return null;
		return trimmed;
	} catch {
		return null;
	}
}

function resolveBootstrapLanguage(): string {
	const fromRedux = readReduxPersistedLanguage();
	const fromLocalStorage = readPersistedLanguage();
	const persisted = fromRedux ?? fromLocalStorage;
	if (persisted && persisted !== LEGACY_DEFAULT_LANGUAGE) {
		return persisted;
	}
	return getDeviceLanguage();
}

/** Apply persisted / device language after redux-persist rehydration (web + native). */
export async function bootstrapI18nLanguage(): Promise<void> {
	const target = resolveBootstrapLanguage();
	if (__DEV__) {
		console.log(
			'[i18n] bootstrapI18nLanguage',
			JSON.stringify({
				fromRedux: readReduxPersistedLanguage(),
				fromLocalStorage: readPersistedLanguage(),
				target,
			})
		);
	}
	try {
		await changeAppLanguage(target);
		if (__DEV__) {
			console.log('[i18n] bootstrapI18nLanguage done', JSON.stringify({ language: i18n.language }));
		}
	} catch (error) {
		console.error('[i18n] bootstrapI18nLanguage failed, falling back to en-GB', error);
		await changeAppLanguage('en-GB');
	}
}

if (!i18nRef.__smartsaasChangeLanguagePatched) {
	const nativeChangeLanguage = getNativeChangeLanguage();
	i18n.changeLanguage = ((lng?: string, ...args: unknown[]) =>
		ensureLocaleLoaded(lng).then((locale) => nativeChangeLanguage(locale, ...args))) as typeof i18n.changeLanguage;
	i18nRef.__smartsaasChangeLanguagePatched = true;
}

export { ensureLocaleLoaded };
export default i18n;
